import {
  Button,
  Card,
  Dialog,
  DialogClose,
  DialogPopup,
  DialogTitle,
  DialogTrigger,
  Field,
  FieldLabel,
  FieldMessage,
  TextField,
} from '@fold-and-flow/ui'
import { useForm } from '@tanstack/react-form'
import { Link } from '@tanstack/react-router'
import {
  type ColumnDef,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { useEffect, useRef, useState } from 'react'
import { z } from 'zod'
import { trpc } from '#lib/trpc'

const clipFormSchema = z.object({
  name: z.string().min(1, 'Clip name is required'),
  description: z.string().max(500),
  durationSeconds: z.number().int().min(10, 'Uploaded video duration is not ready yet'),
  phases: z
    .array(z.enum(['warmup', 'main', 'peak', 'integration', 'savasana']))
    .min(1, 'Select at least one phase'),
  goalTags: z.string(),
  injuryCautions: z.string(),
  intensity: z.enum(['gentle', 'moderate', 'vigorous']),
  muxAssetId: z.string().min(1, 'Upload a video before saving'),
})

const defaultValues: z.infer<typeof clipFormSchema> = {
  name: '',
  description: '',
  durationSeconds: 0,
  phases: [],
  goalTags: '',
  injuryCautions: '',
  intensity: 'moderate',
  muxAssetId: '',
}

type UploadState =
  | { stage: 'idle' }
  | { stage: 'uploading'; progress: number }
  | { stage: 'processing'; uploadId: string }
  | { stage: 'ready'; assetId: string }
  | { stage: 'error'; message: string }

function VideoUploader({
  onReady,
  onReset,
}: {
  onReady: (assetId: string, durationSeconds: number) => void
  onReset: () => void
}) {
  const [upload, setUpload] = useState<UploadState>({ stage: 'idle' })
  const inputRef = useRef<HTMLInputElement>(null)
  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady
  const onResetRef = useRef(onReset)
  onResetRef.current = onReset
  const getUploadUrl = trpc.clips.getUploadUrl.useMutation()

  const uploadId = upload.stage === 'processing' ? upload.uploadId : ''
  const { data: resolveData } = trpc.clips.resolveUpload.useQuery(
    { uploadId },
    {
      enabled: upload.stage === 'processing',
      refetchInterval: (query) =>
        query.state.data?.assetId && query.state.data.durationSeconds !== null ? false : 3000,
    }
  )

  useEffect(() => {
    if (resolveData?.assetId && resolveData.durationSeconds !== null) {
      setUpload({ stage: 'ready', assetId: resolveData.assetId })
      onReadyRef.current(resolveData.assetId, resolveData.durationSeconds)
    }
  }, [resolveData?.assetId, resolveData?.durationSeconds])

  function handleReset() {
    setUpload({ stage: 'idle' })
    onResetRef.current()
  }

  async function handleFile(file: File) {
    onResetRef.current()
    setUpload({ stage: 'uploading', progress: 0 })
    try {
      const { uploadUrl, uploadId } = await getUploadUrl.mutateAsync()

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable)
            setUpload({ stage: 'uploading', progress: Math.round((e.loaded / e.total) * 100) })
        })
        xhr.addEventListener('load', () =>
          xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`))
        )
        xhr.addEventListener('error', () => reject(new Error('Network error during upload')))
        xhr.open('PUT', uploadUrl)
        xhr.setRequestHeader('Content-Type', file.type)
        xhr.send(file)
      })

      setUpload({ stage: 'processing', uploadId })
    } catch (err) {
      setUpload({ stage: 'error', message: err instanceof Error ? err.message : 'Upload failed' })
    }
  }

  if (upload.stage === 'error') {
    return (
      <div className="flex items-center justify-between rounded-xl border border-red-200 bg-red-50 px-4 py-3">
        <p className="text-sm text-red-600">{upload.message}</p>
        <button
          type="button"
          onClick={handleReset}
          className="text-xs text-red-500 hover:text-red-700 underline ml-4 shrink-0"
        >
          Try again
        </button>
      </div>
    )
  }

  if (upload.stage === 'ready') {
    return (
      <div className="flex items-center justify-between">
        <p className="text-xs text-sage-600">✓ Video ready — asset ID: {upload.assetId}</p>
        <button
          type="button"
          onClick={handleReset}
          className="text-xs text-warm-gray-400 hover:text-warm-gray-600"
        >
          Replace
        </button>
      </div>
    )
  }

  if (upload.stage === 'processing') {
    return (
      <div className="flex items-center gap-2 text-sm text-warm-gray-500">
        <span className="animate-spin">⏳</span> Mux is processing your video…
      </div>
    )
  }

  if (upload.stage === 'uploading') {
    return (
      <div className="flex flex-col gap-2">
        <div className="h-1.5 rounded-full bg-cream-200 overflow-hidden">
          <div
            className="h-full bg-terracotta-400 transition-all duration-300 rounded-full"
            style={{ width: `${upload.progress}%` }}
          />
        </div>
        <p className="text-xs text-warm-gray-400 text-center">Uploading… {upload.progress}%</p>
      </div>
    )
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) void handleFile(file)
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="w-full border-2 border-dashed border-cream-200 rounded-xl py-8 flex flex-col items-center gap-2 text-warm-gray-400 hover:border-terracotta-300 hover:text-terracotta-500 transition-colors cursor-pointer"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault()
          const file = e.dataTransfer.files[0]
          if (file) void handleFile(file)
        }}
      >
        <span className="text-2xl">🎬</span>
        <span className="text-sm">Drop a video or click to browse</span>
      </button>
    </>
  )
}

interface Clip {
  id: string
  name: string
  durationSeconds: number
  intensity: string
  phases: string[]
  muxAssetId: string
}

const columnHelper = createColumnHelper<Clip>()

const PHASE_OPTIONS = ['warmup', 'main', 'peak', 'integration', 'savasana'] as const

function formatDuration(seconds: number) {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return s === 0 ? `${m}m` : `${m}m ${s}s`
}

function TrashIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  )
}

function DeleteClipButton({ id, onDeleted }: { id: string; onDeleted: () => void }) {
  const [open, setOpen] = useState(false)
  const deleteClip = trpc.clips.delete.useMutation({
    onSuccess: () => {
      setOpen(false)
      onDeleted()
    },
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <button
            type="button"
            className="text-warm-gray-400 hover:text-red-500 transition-colors"
            aria-label="Delete clip"
          />
        }
      >
        <TrashIcon />
      </DialogTrigger>
      <DialogPopup>
        <DialogTitle>Delete clip?</DialogTitle>
        <DialogClose aria-label="Cancel" />
        <p className="text-sm text-warm-gray-500 mt-2">This cannot be undone.</p>
        <div className="flex gap-3 mt-6">
          <Button variant="ghost" className="flex-1" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            className="flex-1 bg-red-500 hover:bg-red-600 text-white"
            loading={deleteClip.isPending}
            onClick={() => deleteClip.mutate({ id })}
          >
            Delete
          </Button>
        </div>
      </DialogPopup>
    </Dialog>
  )
}

export function AdminClipsPage() {
  const [sorting, setSorting] = useState<SortingState>([])
  const [uploadOpen, setUploadOpen] = useState(false)

  const { data: clips = [], refetch } = trpc.clips.list.useQuery()
  const createClip = trpc.clips.create.useMutation({
    onSuccess: () => {
      void refetch()
      setUploadOpen(false)
      form.reset()
    },
  })

  const form = useForm({
    defaultValues,
    validators: {
      onSubmit: clipFormSchema,
    },
    onSubmit: async ({ value }) => {
      const parsed = clipFormSchema.parse(value)

      await createClip.mutateAsync({
        name: parsed.name,
        description: parsed.description || null,
        durationSeconds: parsed.durationSeconds,
        phases: parsed.phases,
        goalTags: parsed.goalTags
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        energyTags: [],
        injuryCautions: parsed.injuryCautions
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        intensity: parsed.intensity,
        muxAssetId: parsed.muxAssetId,
      })
    },
  })

  function getErrorMessage(error: unknown): string {
    if (error instanceof z.ZodError) {
      return z.prettifyError(error)
    }

    if (typeof error === 'string') {
      return error
    }

    if (error instanceof Error) {
      return error.message
    }

    return 'Invalid value'
  }

  function getFieldError(errors: readonly unknown[], shouldShow: boolean): string | undefined {
    if (!shouldShow || errors.length === 0) {
      return undefined
    }

    return getErrorMessage(errors[0])
  }

  const columns: ColumnDef<Clip>[] = [
    columnHelper.accessor('name', {
      header: 'Name',
      cell: (info) => (
        <Link
          to="/admin/clips/$clipId"
          params={{ clipId: info.row.original.id }}
          className="font-medium text-warm-gray-800 hover:text-terracotta-600 transition-colors"
        >
          {info.getValue()}
        </Link>
      ),
    }),
    columnHelper.accessor('durationSeconds', {
      header: 'Duration',
      cell: (info) => <span className="text-warm-gray-600">{formatDuration(info.getValue())}</span>,
    }),
    columnHelper.accessor('intensity', {
      header: 'Intensity',
      cell: (info) => <span className="capitalize text-warm-gray-600">{info.getValue()}</span>,
    }),
    columnHelper.accessor('phases', {
      header: 'Phases',
      cell: (info) => (
        <div className="flex flex-wrap gap-1">
          {info.getValue().map((p) => (
            <span
              key={p}
              className="px-2 py-0.5 rounded-full bg-sage-400/10 text-sage-700 text-xs capitalize"
            >
              {p}
            </span>
          ))}
        </div>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      cell: ({ row }) => <DeleteClipButton id={row.original.id} onDeleted={() => void refetch()} />,
    }),
  ]

  const table = useReactTable({
    data: clips,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl text-warm-gray-900">Video Clips</h1>
          <p className="text-warm-gray-400 mt-1">{clips.length} clips in library</p>
        </div>
        <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
          <DialogTrigger render={<Button />}>+ Add clip</DialogTrigger>
          <DialogPopup>
            <DialogTitle>Add video clip</DialogTitle>
            <DialogClose aria-label="Close" />
            <form
              onSubmit={(e) => {
                e.preventDefault()
                void form.handleSubmit()
              }}
              className="flex flex-col gap-4 mt-4"
            >
              <form.Field name="name">
                {(field) => (
                  <TextField
                    label="Clip name"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={getFieldError(
                      field.state.meta.errors,
                      field.state.meta.isTouched || form.state.submissionAttempts > 0
                    )}
                    required
                  />
                )}
              </form.Field>

              <form.Field name="phases">
                {(field) => {
                  const error = getFieldError(
                    field.state.meta.errors,
                    field.state.meta.isTouched || form.state.submissionAttempts > 0
                  )

                  return (
                    <Field>
                      <FieldLabel>Phases</FieldLabel>
                      <div className="flex flex-wrap gap-2">
                        {PHASE_OPTIONS.map((p) => {
                          const selected = field.state.value.includes(p)
                          const handleTogglePhase = () => {
                            field.handleChange(
                              selected
                                ? field.state.value.filter((v) => v !== p)
                                : [...field.state.value, p]
                            )
                          }

                          return (
                            <button
                              key={p}
                              type="button"
                              onClick={handleTogglePhase}
                              className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                                selected
                                  ? 'border-terracotta-400 bg-terracotta-400/5 text-terracotta-600'
                                  : 'border-cream-200 text-warm-gray-600 hover:border-warm-gray-400'
                              } ${error ? 'border-red-400' : ''}`}
                            >
                              {p}
                            </button>
                          )
                        })}
                      </div>
                      <FieldMessage>{error}</FieldMessage>
                    </Field>
                  )
                }}
              </form.Field>

              <form.Field name="intensity">
                {(field) => (
                  <Field>
                    <FieldLabel>Intensity</FieldLabel>
                    <div className="flex gap-2">
                      {(['gentle', 'moderate', 'vigorous'] as const).map((level) => {
                        const handleSelectIntensity = () => field.handleChange(level)

                        return (
                          <button
                            key={level}
                            type="button"
                            onClick={handleSelectIntensity}
                            className={`flex-1 py-2 rounded-xl text-xs border capitalize transition-all ${
                              field.state.value === level
                                ? 'border-terracotta-400 bg-terracotta-400/5 text-terracotta-600 font-medium'
                                : 'border-cream-200 text-warm-gray-600 hover:border-warm-gray-400'
                            }`}
                          >
                            {level}
                          </button>
                        )
                      })}
                    </div>
                  </Field>
                )}
              </form.Field>

              <form.Field name="goalTags">
                {(field) => (
                  <TextField
                    label="Goal tags (comma-separated)"
                    placeholder="hips, flexibility, stress-relief"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={getFieldError(
                      field.state.meta.errors,
                      field.state.meta.isTouched || form.state.submissionAttempts > 0
                    )}
                  />
                )}
              </form.Field>

              <form.Field name="injuryCautions">
                {(field) => (
                  <TextField
                    label="Injury cautions (comma-separated)"
                    placeholder="knee, wrist"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={getFieldError(
                      field.state.meta.errors,
                      field.state.meta.isTouched || form.state.submissionAttempts > 0
                    )}
                  />
                )}
              </form.Field>

              <form.Field name="muxAssetId">
                {(field) => {
                  const assetError = getFieldError(
                    field.state.meta.errors,
                    field.state.meta.isTouched || form.state.submissionAttempts > 0
                  )

                  return (
                    <form.Field name="durationSeconds">
                      {(durationField) => {
                        const durationError = getFieldError(
                          durationField.state.meta.errors,
                          durationField.state.meta.isTouched || form.state.submissionAttempts > 0
                        )

                        return (
                          <Field className="rounded-xl border border-cream-200 p-4">
                            <FieldLabel>Video</FieldLabel>
                            <VideoUploader
                              onReady={(id, duration) => {
                                field.handleChange(id)
                                durationField.handleChange(duration)
                              }}
                              onReset={() => {
                                field.handleChange('')
                                durationField.handleChange(0)
                              }}
                            />
                            <FieldMessage>{assetError ?? durationError}</FieldMessage>
                          </Field>
                        )
                      }}
                    </form.Field>
                  )
                }}
              </form.Field>

              {createClip.error ? (
                <FieldMessage>{getErrorMessage(createClip.error)}</FieldMessage>
              ) : null}

              <form.Subscribe selector={(s) => s.isSubmitting}>
                {(isSubmitting) => (
                  <Button type="submit" loading={isSubmitting} className="mt-2">
                    Save clip
                  </Button>
                )}
              </form.Subscribe>
            </form>
          </DialogPopup>
        </Dialog>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id} className="border-b border-cream-200">
                  {hg.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-6 py-3 text-left text-xs font-medium text-warm-gray-400 uppercase tracking-wide cursor-pointer select-none"
                      onClick={header.column.getToggleSortingHandler()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ')
                          header.column.getToggleSortingHandler()?.(e)
                      }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getIsSorted() === 'asc'
                        ? ' ↑'
                        : header.column.getIsSorted() === 'desc'
                          ? ' ↓'
                          : ''}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-warm-gray-400">
                    No clips yet. Add your first clip above.
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-cream-100 hover:bg-cream-50 transition-colors"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-6 py-4">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}
